import api from './api';

export interface SocialConnection {
    id: string;
    provider: string;
    provider_user_id: string;
    created_at: string;
}

export interface Reply {
    id: string;
    text: string;
    author: string;
    author_id: string;
    timestamp: string;
}

export interface Comment {
    id: string;
    platform: 'facebook' | 'instagram' | 'tiktok' | 'youtube';
    text: string;
    author: string;
    author_id: string;
    timestamp: string;
    post_title: string;
    post_id: string;
    replies?: Reply[];
}

class SocialService {
    async getConnections(): Promise<SocialConnection[]> {
        const response = await api.get('/social/');
        return response.data;
    }

    async connectFacebook(): Promise<{ auth_url: string }> {
        const response = await api.get('/social/connect/facebook');
        return response.data;
    }

    async handleFacebookCallback(code: string, state: string): Promise<SocialConnection> {
        const response = await api.get(`/social/callback/facebook?code=${code}&state=${state}`);
        return response.data;
    }

    async connectInstagram(): Promise<{ auth_url: string }> {
        const response = await api.get('/social/connect/instagram');
        return response.data;
    }

    async handleInstagramCallback(code: string, state: string): Promise<SocialConnection> {
        const response = await api.get(`/social/callback/instagram?code=${code}&state=${state}`);
        return response.data;
    }

    async postToInstagram(postId: string, imageUrls: string[], caption: string, contentType: string = 'feed'): Promise<any> {
        const response = await api.post('/social/post/instagram', { 
            post_id: postId,
            image_urls: imageUrls, 
            caption: caption,
            content_type: contentType
        });
        return response.data;
    }

    async postToFacebook(message: string, imageUrl?: string): Promise<any> {
        const response = await api.post('/social/post/facebook', { 
            message: message,
            image_url: imageUrl
        });
        return response.data;
    }

    async postToYouTube(postId: string, videoUrl: string, title: string, description: string = '', tags: string[] = [], privacyStatus: string = 'private'): Promise<any> {
        const response = await api.post('/social/post/youtube', {
            post_id: postId,
            video_url: videoUrl,
            title: title,
            description: description,
            tags: tags,
            privacy_status: privacyStatus,
            category_id: '22' // People & Blogs
        });
        return response.data;
    }

    // Placeholder for new social media platforms - requires backend implementation
    async connectTwitter(): Promise<{ auth_url: string }> {
        const response = await api.get('/social/connect/twitter');
        return response.data;
    }

    async handleTwitterCallback(code: string): Promise<SocialConnection> {
        const response = await api.get(`/social/callback/twitter?code=${code}`);
        return response.data;
    }

    async connectLinkedIn(): Promise<{ auth_url: string }> {
        const response = await api.get('/social/connect/linkedin');
        return response.data;
    }

    async handleLinkedInCallback(code: string): Promise<SocialConnection> {
        const response = await api.get(`/social/callback/linkedin?code=${code}`);
        return response.data;
    }

    async connectYouTube(): Promise<{ auth_url: string }> {
        const response = await api.get('/social/connect/youtube');
        return response.data;
    }

    async handleYouTubeCallback(code: string): Promise<SocialConnection> {
        const response = await api.get(`/social/callback/youtube?code=${code}`);
        return response.data;
    }

    async connectTwitter(): Promise<{ auth_url: string }> {
        const response = await api.get('/social/connect/twitter');
        return response.data;
    }

    async handleTwitterCallback(code: string, state: string): Promise<SocialConnection> {
        const response = await api.get(`/social/callback/twitter?code=${code}&state=${state}`);
        return response.data;
    }

    async connectPinterest(): Promise<{ auth_url: string }> {
        const response = await api.get('/social/connect/pinterest');
        return response.data;
    }

    async handlePinterestCallback(code: string): Promise<SocialConnection> {
        const response = await api.get(`/social/callback/pinterest?code=${code}`);
        return response.data;
    }

    async getComments(platform?: string, limit: number = 10, days?: number): Promise<Comment[]> {
        const params = new URLSearchParams();
        if (platform && platform !== 'all') {
            params.append('platform', platform);
        }
        params.append('limit', limit.toString());
        if (days) {
            params.append('days', days.toString());
        }
        
        const response = await api.get(`/social/comments?${params.toString()}`);
        return response.data;
    }

    async replyToComment(commentId: string, message: string, platform: string): Promise<any> {
        const response = await api.post('/social/comments/reply', {
            comment_id: commentId,
            message: message,
            platform: platform
        });
        return response.data;
    }

    async deleteConnection(provider: string): Promise<void> {
        await api.delete(`/social/${provider}`);
    }
}

export const socialService = new SocialService();
